import {
  faCheckCircle,
  faInfoCircle,
  faSpinner,
  faWarning,
  faXmarkCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { palette } from '@ui-lib/color';
import { fontWeights } from '@ui-lib/css';
import { palettenext } from 'ui-lib/src/lib/color/palette-next';

type NotificationTone = 'positive' | 'critical' | 'info' | 'caution' | 'black' | 'loading';
type NotificationSize = 'default' | 'lg' | 'mini';

/** Content passed to showNotification — size/tone come from the outer NotificationProps. */
interface NotificationContentProps {
  content: string;
  buttonContent?: string;
  buttonIcon?: JSX.Element;
  buttonOnClick?: () => void;
}

/** Internal props for the NotificationContent component; size/tone are threaded in by show/updateNotification. */
type NotificationContentInternalProps = NotificationContentProps & {
  size?: NotificationSize;
  tone?: NotificationTone;
};

export interface NotificationProps {
  id?: string;
  tone?: NotificationTone;
  notificationContent: NotificationContentProps;
  onClose?: () => void;
  /** Set to false to prevent auto-close. Defaults to Mantine's default (auto-close after timeout). */
  autoClose?: boolean | number;
  /** Notification position. Defaults to 'bottom-center'. */
  position?:
    | 'top-left'
    | 'top-right'
    | 'top-center'
    | 'bottom-left'
    | 'bottom-right'
    | 'bottom-center';
  /** Notification size. 'lg' gives more padding, larger text, and wider container. */
  size?: NotificationSize;
}

interface SizeConfig {
  width: string;
  textSize: string;
  buttonSize: string;
  padding: string | undefined;
  /** Override notification radius. Defaults to 'lg'. */
  radius?: 'lg' | number;
  /** Override close-button visibility. Defaults to true. */
  withCloseButton?: boolean;
}

const SIZE_CONFIG: Record<NotificationSize, SizeConfig> = {
  default: { width: '500px', textSize: 'sm', buttonSize: 'xs', padding: undefined },
  lg: { width: '520px', textSize: 'md', buttonSize: 'sm', padding: '16px' },
  mini: {
    width: 'fit-content',
    textSize: 'md',
    buttonSize: 'xs',
    padding: '0.75rem 1.5rem',
    radius: 9999,
    withCloseButton: false,
  },
};

const BLACK_TONE_STYLE = {
  width: 'fit-content',
  padding: '0.75rem 1.125rem',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
} as const;

const BLACK_TONE_STYLES = {
  icon: { width: 14, height: 14, marginInlineEnd: 10, fontSize: 14 },
  body: { marginInlineEnd: 0 },
} as const;

const NotificationContent = ({
  size = 'default',
  tone,
  ...props
}: NotificationContentInternalProps) => {
  const config = SIZE_CONFIG[size];
  const isBlack = tone === 'black';
  return (
    <Group justify="space-between" align="center" gap="md" wrap="nowrap">
      <Text
        size={config.textSize}
        c={isBlack ? palettenext.white : undefined}
        fw={isBlack ? fontWeights.bold : undefined}
        lh={isBlack ? '18px' : undefined}
        style={isBlack ? { whiteSpace: 'nowrap' } : undefined}
      >
        {props.content}
      </Text>
      {props.buttonContent && tone !== 'black' && (
        <Button
          leftSection={props.buttonIcon}
          onClick={props.buttonOnClick}
          size={config.buttonSize}
          style={{ flexShrink: 0 }}
        >
          {props.buttonContent}
        </Button>
      )}
    </Group>
  );
};

const notificationBackgroundColor = (tone?: NotificationTone) => {
  switch (tone) {
    case 'black':
      return palettenext.black;
    case 'positive':
    case 'loading':
      return palettenext.green[50];
    case 'critical':
      return palettenext.red[50];
    case 'caution':
      return palettenext.yellow[50];
    case 'info':
    default:
      return palettenext.blue[50];
  }
};

const notificationIcon = (tone?: NotificationTone) => {
  const color = tone === 'black' ? palette.white : palette.black;
  switch (tone) {
    case 'positive':
    case 'black':
      return <FontAwesomeIcon icon={faCheckCircle} style={{ color }} />;
    case 'critical':
      return <FontAwesomeIcon icon={faXmarkCircle} style={{ color }} />;
    case 'caution':
      return <FontAwesomeIcon icon={faWarning} style={{ color }} />;
    case 'loading':
      return <FontAwesomeIcon icon={faSpinner} spin style={{ color }} />;
    case 'info':
    default:
      return <FontAwesomeIcon icon={faInfoCircle} style={{ color }} />;
  }
};

export const showNotification = ({ onClose, ...props }: NotificationProps) => {
  const size = props.size ?? 'default';
  const config = SIZE_CONFIG[size];
  const backgroundColor = notificationBackgroundColor(props.tone);
  const isBlack = props.tone === 'black';
  notifications.show({
    id: props.id,
    icon: notificationIcon(props.tone),
    color: 'transparent',
    position: props.position ?? 'bottom-center',
    radius: isBlack ? 'large' : 'lg',
    withCloseButton: !isBlack,
    styles: isBlack ? BLACK_TONE_STYLES : undefined,
    message: <NotificationContent {...props.notificationContent} size={size} tone={props.tone} />,
    style: {
      backgroundColor,
      width: config.width,
      ...(config.padding ? { padding: config.padding } : {}),
      ...(isBlack ? BLACK_TONE_STYLE : {}),
    },
    onClose,
    autoClose: props.autoClose,
  });
};

export const updateNotification = (id: string, props: Omit<NotificationProps, 'id'>) => {
  const size = props.size ?? 'default';
  const config = SIZE_CONFIG[size];
  const backgroundColor = notificationBackgroundColor(props.tone);
  notifications.update({
    id,
    icon: notificationIcon(props.tone),
    color: 'transparent',
    position: props.position ?? 'bottom-center',
    radius: config.radius ?? 'lg',
    withCloseButton: config.withCloseButton ?? true,
    message: <NotificationContent {...props.notificationContent} size={size} tone={props.tone} />,
    style: {
      backgroundColor,
      width: config.width,
      ...(config.padding ? { padding: config.padding } : {}),
    },
    onClose: props.onClose,
    autoClose: props.autoClose,
  });
};

export const hideNotification = (id: string) => {
  notifications.hide(id);
};
